import { Box, Button, GlobalStyles, MenuItem, Select, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { DARK_GREY } from '../styles/colors';
import { getLang, makeT } from '../l10n';
import patternBgPng from '../assets/certificate/pattern_bg.png';
import funexpectedLogoSvg from '../assets/certificate/funexpected_logo.svg';
import signLineSvg from '../assets/certificate/sign_line.svg';
import signatureSvg from '../assets/certificate/signature.svg';
import starBadge1Png from '../assets/certificate/star_badge_1.png';
import starBadge2Png from '../assets/certificate/star_badge_2.png';
import starBadge3Png from '../assets/certificate/star_badge_3.png';
import starBadge4Png from '../assets/certificate/star_badge_4.png';
import starBadge5Png from '../assets/certificate/star_badge_5.png';

const MODULE_THEMES = {
  1: { color: '#34B778', badge: starBadge1Png },
  2: { color: '#EF81AC', badge: starBadge2Png },
  3: { color: '#60A8EF', badge: starBadge3Png },
  4: { color: '#F26D6D', badge: starBadge4Png },
  5: { color: '#6A56CC', badge: starBadge5Png },
};

const CARD = 1080;
const INNER = 72;
const LINE_W = CARD - INNER * 2; // 936
const MARGIN = 100;
const CONTROL_WIDTH = 220;

const LANG_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
  { code: 'pt-BR', label: 'Português (BR)' },
  { code: 'nl-NL', label: 'Nederlands' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'sv', label: 'Svenska' },
  { code: 'ja', label: '日本語' },
  { code: 'ko', label: '한국어' },
  { code: 'zh-Hans', label: '简体中文' },
  { code: 'zh-Hant', label: '繁體中文' },
  { code: 'zh-HK', label: '繁體中文 (HK)' },
  { code: 'it', label: 'Italiano' },
  { code: 'pl', label: 'Polski' },
  { code: 'tr', label: 'Türkçe' },
];

const printStyles = (
  <GlobalStyles
    styles={{
      'html, body': { margin: 0, height: '100%', overflow: 'hidden' },
      '@media print': {
        '@page': {
          size: 'letter portrait',
          margin: '10mm'
        },
        '*': {
          WebkitPrintColorAdjust: 'exact !important',
          printColorAdjust: 'exact !important',
        },
        'html, body': { overflow: 'visible !important', background: 'white !important' },
        '#certificate-outer': {
          background: 'white !important',
        },
        '#certificate-wrapper': {
          width: '100vw !important',
          height: '100vw !important',
        },
        '#certificate-card': {
          transform: 'none !important',
          zoom: '55%',
        },
        '#cert-controls': { display: 'none !important' },
      },
    }}
  />
);

const TopicLine = ({ label, count, tasksLabel }) => (
  <Box sx={{ position: 'relative', height: 84, width: LINE_W }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '100%',
        fontFamily: '"Geometria", sans-serif',
        fontWeight: 400,
        fontSize: 30,
        color: DARK_GREY,
        whiteSpace: 'nowrap',
      }}
    >
      <span>{label}</span>
      <span style={{ textTransform: 'lowercase' }}>
        {count.toLocaleString()} {tasksLabel}
      </span>
    </Box>
  </Box>
);
TopicLine.propTypes = {
  label: PropTypes.string,
  count: PropTypes.number,
  tasksLabel: PropTypes.string,
};

export const Certificate = () => {
  const { payload } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = getLang(searchParams.toString());
  const t = makeT(lang);
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const data = JSON.parse(atob(padded));
  const name = data.n || 'Luca';
  const tasks = data.t || [9300, 340, 120];
  const module = data.m || 1;
  const theme = MODULE_THEMES[module] || MODULE_THEMES[1];
  const issuedAt = typeof data.d === 'number' ? new Date(data.d * 1000) : null;
  const issuedAtLabel = issuedAt
    ? issuedAt.toLocaleDateString(lang, { year: 'numeric', month: 'long', day: 'numeric' })
    : '';
  const [numeracy, geometry, logic] = tasks;
  const total = tasks.reduce((a, b) => a + b, 0).toLocaleString();
  const tasksLabel = t('TASKS');

  const [scale, setScale] = useState(1);
  useEffect(() => {
    const screenScale = () => Math.min(
      (window.innerWidth - MARGIN * 2) / CARD,
      (window.innerHeight - MARGIN * 2) / CARD,
    );

    setScale(screenScale());
    const handleResize = () => setScale(screenScale());
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {printStyles}
      <Box
        id="cert-controls"
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: CONTROL_WIDTH,
        }}
      >
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          sx={{
            width: '100%',
            bgcolor: DARK_GREY,
            color: '#ffffff',
            textTransform: 'none',
            fontFamily: '"Geometria", sans-serif',
            fontWeight: 400,
            '&:hover': { bgcolor: '#333333' },
          }}
        >
          Print / Save as PDF
        </Button>
        <Select
          value={lang}
          onChange={(e) => {
            const next = new URLSearchParams(searchParams);
            next.set('lang', e.target.value);
            setSearchParams(next, { replace: true });
          }}
          sx={{
            width: '100%',
            bgcolor: '#ffffff',
            fontFamily: '"Geometria", sans-serif',
            fontSize: 14,
            '& .MuiSelect-select': { py: '8px' },
          }}
          size="small"
        >
          {LANG_OPTIONS.map((opt) => (
            <MenuItem key={opt.code} value={opt.code}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box
        id="certificate-outer"
        sx={{
          width: '100vw',
          height: '100vh',
          bgcolor: '#f1f1f1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          id="certificate-wrapper"
          sx={{
            width: scale * CARD + MARGIN * 2,
            height: scale * CARD + MARGIN * 2,
            backgroundColor: '#f1f1f1',
            backgroundImage: `linear-gradient(rgba(241,241,241,0.7), rgba(241,241,241,0.7)), url(${patternBgPng})`,
            backgroundRepeat: 'repeat',
            backgroundSize: 'auto, 512px 512px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
        <Box
          id="certificate-card"
          sx={{
            position: 'relative',
            width: CARD,
            height: CARD,
            bgcolor: '#ffffff',
            flexShrink: 0,
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
          }}
        >
          {/* Header text block */}
          <Box
            sx={{
              position: 'absolute',
              top: 36,
              left: 48,
              width: CARD - 48 * 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              textAlign: 'center',
              lineHeight: 1.2,
            }}
          >
            <Typography
              sx={{
                fontFamily: '"GeometriaExtraBold", "Geometria", sans-serif',
                fontWeight: 800,
                fontSize: 30,
                letterSpacing: '2px',
                textTransform: 'uppercase',
                color: DARK_GREY,
              }}
            >
              {t('MIS-CERTIFICATE-1')}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Geometria", sans-serif',
                fontWeight: 400,
                fontSize: 30,
                color: DARK_GREY,
              }}
            >
              {t('MIS-CERTIFICATE-2')}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"GeometriaExtraBold", "Geometria", sans-serif',
                fontWeight: 800,
                fontSize: 44,
                color: theme.color,
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Geometria", sans-serif',
                fontWeight: 400,
                fontSize: 30,
                color: DARK_GREY,
              }}
            >
              {t('MIS-CERTIFICATE-4', { number: total, 'module name': `Module\u00A0${module}` })}
            </Typography>
          </Box>

          {/* Topic rows */}
          <Box
            sx={{
              position: 'absolute',
              top: 340,
              left: INNER,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <TopicLine label={t('MIS-CERTIFICATE-SUBJECT-1')} count={numeracy} tasksLabel={tasksLabel} />
            <TopicLine label={t('MIS-CERTIFICATE-SUBJECT-2')} count={geometry} tasksLabel={tasksLabel} />
            <TopicLine label={t('MIS-CERTIFICATE-SUBJECT-3')} count={logic} tasksLabel={tasksLabel} />
          </Box>

          {/* Star badge */}
          <Box
            component="img"
            src={theme.badge}
            sx={{ position: 'absolute', bottom: 144, left: INNER, width: 288, height: 288 }}
          />

          {/* Signature block — vertically aligned with the star badge */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 144,
              right: INNER,
              width: 432,
              height: 288,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Box
              component="img"
              src={signatureSvg}
              sx={{ width: 315, height: 75, display: 'block', mb: '4px' }}
            />
            <Box sx={{ position: 'relative', width: 288, textAlign: 'center' }}>
              <Box
                component="img"
                src={signLineSvg}
                sx={{ width: '100%', height: 2, display: 'block', mb: '6px' }}
              />
              <Typography
                sx={{
                  fontFamily: '"Geometria", sans-serif',
                  fontWeight: 400,
                  fontSize: 20,
                  color: DARK_GREY,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                }}
              >
                {t('MIS-CERTIFICATE-5')}
              </Typography>
            </Box>
            <Box
              component="img"
              src={funexpectedLogoSvg}
              sx={{ width: 291, height: 27, display: 'block', mt: '14px' }}
            />
            {issuedAtLabel && (
              <Typography
                sx={{
                  fontFamily: '"GeometriaExtraBold", "Geometria", sans-serif',
                  fontWeight: 800,
                  fontSize: 27,
                  lineHeight: 1.2,
                  color: theme.color,
                  whiteSpace: 'nowrap',
                  mt: '20px',
                }}
              >
                {issuedAtLabel}
              </Typography>
            )}
          </Box>
        </Box>
        </Box>
      </Box>
    </>
  );
};
